/**
 * Audience exclusion helpers (visibility-only).
 *
 * Mirrors the server eligibility rule in compute_flight_audience_stats so the UI
 * can label and summarize WHY recipients were excluded from a flight send.
 *
 * Eligibility (preserved, do NOT change): a contact is eligible iff it has an
 * email AND is not suppressed AND email_opt_in IS NOT FALSE (NULL counts as
 * eligible) AND email_opt_out_at IS NULL. Reasons are mutually exclusive with
 * precedence missing_email -> suppressed -> email_opt_out (matching the SQL).
 */

export type ExclusionReasonKey =
  | "email_opt_out"
  | "suppressed"
  | "missing_email"
  | "neverbounce_invalid"
  | "neverbounce_risky"
  | "audience_filter";

export const EXCLUSION_REASON_LABELS: Record<ExclusionReasonKey, string> = {
  email_opt_out: "Opted out of email",
  suppressed: "Email suppressed",
  missing_email: "No email on file",
  // Always-on NeverBounce guard: confirmed-undeliverable ('invalid') addresses
  // are dropped from every send to protect sender reputation.
  neverbounce_invalid: "Undeliverable (NeverBounce)",
  // Per-flight opt-out only: 'risky' (unverified) addresses, dropped when the
  // flight turned on "Exclude risky addresses". 0/absent otherwise.
  neverbounce_risky: "Unverified / risky (NeverBounce)",
  audience_filter: "Excluded by audience filter",
};

export interface ContactEligibilityFields {
  email?: string | null;
  email_opt_in?: boolean | null;
  is_email_suppressed?: boolean | null;
  email_opt_out_at?: string | null;
}

/**
 * Classify a contact's send exclusion reason, or null if eligible.
 * Precedence matches the SQL: missing_email -> suppressed -> email_opt_out.
 * (audience_filter is evaluated separately by the filter pipeline, not here.)
 */
export function classifyContactExclusion(
  c: ContactEligibilityFields,
): Exclude<ExclusionReasonKey, "audience_filter"> | null {
  if (!c.email) {
    return "missing_email";
  }
  if (c.is_email_suppressed === true) {
    return "suppressed";
  }
  if (c.email_opt_in === false || (c.email_opt_out_at ?? null) !== null) {
    return "email_opt_out";
  }
  return null; // eligible (incl. email_opt_in === null/true)
}

export interface AudienceStatsLike {
  rawAudienceCount?: number;
  eligibleCount?: number;
  excludedTotal?: number;
  reasons?: Partial<Record<ExclusionReasonKey, number>>;
  examples?: { email: string; reason: string }[];
  examplesOmitted?: boolean;
  // legacy fields (older server payloads)
  originalCount?: number;
  suppressedCount?: number;
  excludedCount?: number;
  // Always-on NeverBounce invalid guard (count parity with the send path).
  invalidEmailDroppedCount?: number;
  // True when the best-effort preview lookup could not confirm invalids; the
  // authoritative fail-closed guard still runs at dispatch.
  invalidGuardLookupFailed?: boolean;
  // Per-flight risky opt-out (2026-08-23): # risky addresses dropped (0 when off).
  riskyEmailDroppedCount?: number;
  // Echo of the flight's risky opt-out state for this count.
  excludeRisky?: boolean;
  // Verdict composition of the audience (post-suppression base), for the
  // "X valid · Y risky · Z invalid" breakdown line.
  verdictBreakdown?: {
    valid?: number;
    risky?: number;
    invalid?: number;
    unvalidated?: number;
  };
}

export interface ExclusionSummary {
  raw: number;
  eligible: number;
  excludedTotal: number;
  reasons: { key: ExclusionReasonKey; label: string; count: number }[];
  hasBreakdown: boolean;
  examples?: { email: string; reason: string; label: string }[];
  examplesOmitted: boolean;
}

/**
 * Build a display-ready exclusion summary from a server stats payload (from
 * flight-audience-count or persisted metadata.audience_exclusions). Tolerates
 * older payloads that lack the reason breakdown.
 */
export function summarizeAudienceStats(
  stats: AudienceStatsLike | null | undefined,
): ExclusionSummary | null {
  if (!stats) {
    return null;
  }
  const raw = stats.rawAudienceCount ?? stats.originalCount ?? 0;
  const eligible = stats.eligibleCount ?? 0;
  const excludedTotal =
    stats.excludedTotal ??
    (stats.rawAudienceCount !== undefined ? raw - eligible : undefined) ??
    (stats.suppressedCount ?? 0) + (stats.excludedCount ?? 0);

  const r = stats.reasons;
  const reasons: ExclusionSummary["reasons"] = [];
  if (r) {
    (Object.keys(EXCLUSION_REASON_LABELS) as ExclusionReasonKey[]).forEach(
      (key) => {
        const count = r[key] ?? 0;
        if (count > 0) {
          reasons.push({ key, label: EXCLUSION_REASON_LABELS[key], count });
        }
      },
    );
  }

  return {
    raw,
    eligible,
    excludedTotal,
    reasons,
    hasBreakdown: reasons.length > 0,
    examples: stats.examples?.map((e) => ({
      ...e,
      label:
        EXCLUSION_REASON_LABELS[e.reason as ExclusionReasonKey] ?? e.reason,
    })),
    examplesOmitted: stats.examplesOmitted ?? false,
  };
}
